import React, { useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import { CardTitle } from '../../../../components/card/NewCard'
import Checkbox from '../../../../components/forms/Checkbox'
import NumberField from '../../../../components/forms/NumberField'
import RadioButtons from '../../../../components/forms/RadioButtons'
import Button from '../../../../components/forms/Button'
import { StyledFieldSet, StyledLegend } from '../../../../components/StyledElements'
import { hasValue } from '../../../../utils/NumberUtil'
import useLocalStorage from '../../../../utils/customHook/useLocalStorage'
import { CHARTOPTIONS_TAB, PLUVIO_FOLLOW_CHART_OPTIONS } from '../constants/PluvioChartTypes'

const DEFAULT_OPTIONS = {
    displayXGrid: true,
    displayYGrid: true,
    yScaleMode: 'auto', // 'auto' | 'manual'
    yMin: null,
    yMax: null,
    yInterval: null,
    modeDate: 'allDate', // 'allDate' | 'dailyDate'
}

const PluvioSuiviChartOptionsTab = ({
    tab,
    pluviometerId,
    changeParent = () => {},
}) => {
    const storageKey = `${PLUVIO_FOLLOW_CHART_OPTIONS}_${pluviometerId}`
    const [chartOptions, setChartOptions] = useLocalStorage(storageKey, DEFAULT_OPTIONS)
    const [newOptions, setNewOptions] = useState(chartOptions)
    const [isValidateActive, setIsValidateActive] = useState(false)

    const change = (changes) => {
        setNewOptions(prev => ({ ...prev, ...changes }))
        setIsValidateActive(true)
    }

    const onCancel = () => {
        setNewOptions(chartOptions)
        setIsValidateActive(false)
    }

    const onApply = () => {
        setChartOptions(newOptions)
        changeParent(newOptions)
        setIsValidateActive(false)
    }

    const scales = [
        { value: 'auto', label: i18n.automatic },
        { value: 'manual', label: i18n.manual },
    ]
    const isManual = newOptions.yScaleMode === 'manual'

    if (tab !== CHARTOPTIONS_TAB) return null

    return (
        <Card>
            <CardTitle title={i18n.chartOptions} />
            <CardContent>
                <Stack spacing={1}>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.gridsDisplay}</StyledLegend>
                        <Grid container>
                            <Grid size={6}>
                                <Checkbox
                                    checked={newOptions.displayXGrid}
                                    label={i18n.Xgrid}
                                    onChange={v => change({ displayXGrid: v })}
                                />
                            </Grid>
                            <Grid size={6}>
                                <Checkbox
                                    checked={newOptions.displayYGrid}
                                    label={i18n.Ygrid}
                                    onChange={v => change({ displayYGrid: v })}
                                />
                            </Grid>
                        </Grid>
                    </StyledFieldSet>
                    <StyledFieldSet>
                        <StyledLegend>{i18n.yScale}</StyledLegend>
                        <RadioButtons
                            elements={scales}
                            selected={newOptions.yScaleMode}
                            onChange={v => {
                                if (v === 'auto') {
                                    change({ yScaleMode: 'auto', yMin: null, yMax: null, yInterval: null })
                                } else {
                                    change({ yScaleMode: 'manual' })
                                }
                            }}
                        />
                        <NumberField
                            value={newOptions.yInterval}
                            min={1}
                            title={i18n.intervalCount}
                            onChange={v => change({ yInterval: hasValue(v) ? v : null })}
                            disabled={!isManual}
                        />
                        <NumberField
                            disabled={!isManual}
                            value={newOptions.yMax}
                            title={i18n.max}
                            onChange={v => change({ yMax: hasValue(v) ? v : null })}
                            floatValue
                        />
                        <NumberField
                            disabled={!isManual}
                            value={newOptions.yMin}
                            title={i18n.min}
                            onChange={v => change({ yMin: hasValue(v) ? v : null })}
                            floatValue
                        />
                    </StyledFieldSet>
                    <Grid container justifyContent='center'>
                        <Button
                            tooltip={i18n.cancel}
                            onClick={onCancel}
                            icon='cancel'
                            className='red btn-floating btn-large margin-left-2 margin-right-2 z-index-10 '
                            disabled={!isValidateActive}
                        />
                        <Button
                            tooltip={i18n.register}
                            onClick={onApply}
                            icon='save'
                            disabled={!isValidateActive}
                            className={`btn-floating btn-large z-index-10 ${isValidateActive ? 'pulse' : ''}`}
                        />
                    </Grid>
                </Stack>
            </CardContent>
        </Card>
    )
}

PluvioSuiviChartOptionsTab.propTypes = {
    tab: PropTypes.string,
    pluviometerId: PropTypes.number,
    changeParent: PropTypes.func,
}

export default PluvioSuiviChartOptionsTab